import React, { useEffect, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { SITE_ORIGIN } from '../seo/site';
import './AboutPage.css';
import './PrivacyPolicyPage.css';

const SECTION_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

function sectionKey(n: number, part: 'title' | 'body'): string {
  const id = n < 10 ? `0${n}` : String(n);
  return `legal.privacy.s${id}_${part}`;
}

function BodyParagraphs({ text }: { text: string }) {
  const parts = text
    .split(/\n\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <>
      {parts.map((paragraph, i) => (
        <p key={i} className="about-marketing-p">
          {paragraph}
        </p>
      ))}
    </>
  );
}

const PrivacyPolicyPage: React.FC = () => {
  const { t, ready, i18n } = useSafeTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const raw = searchParams.get('lang')?.toLowerCase();
    if (raw === 'en' || raw === 'pt') {
      void i18n.changeLanguage(raw);
    }
  }, [searchParams, i18n]);

  const safeT = (key: string, fallback: string) => t(key, fallback);
  const interp = useMemo(() => ({ siteUrl: SITE_ORIGIN }), []);

  if (!ready) {
    return (
      <div className="about-page about-page--loading">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="about-page">
      <Container fluid className="about-page-inner px-3 px-md-4 py-4">
        <nav className="about-back-nav" aria-label={safeT('legal.privacy.navAria', 'Privacy policy')}>
          <Link to="/" className="about-back-link">
            ← {safeT('seo.about.backHome', 'Back to home')}
          </Link>
        </nav>

        <article className="about-marketing-section" aria-labelledby="privacy-main-heading">
          <p className="about-marketing-p text-muted small mb-2">
            {safeT('legal.privacy.updatedLine', 'Last updated: May 4, 2026')}
          </p>

          <div className="privacy-lang-switch mb-3" role="navigation" aria-label={safeT('legal.privacy.langAria', 'Policy language')}>
            <span className="privacy-lang-switch__label me-2">{safeT('legal.privacy.langLabel', 'Language:')}</span>
            <Link
              to="/privacy?lang=en"
              className={`privacy-lang-switch__link${i18n.language?.toLowerCase().startsWith('en') ? ' is-active' : ''}`}
            >
              {safeT('legal.privacy.langEnglish', 'English')}
            </Link>
            <span className="privacy-lang-switch__sep mx-1" aria-hidden>
              ·
            </span>
            <Link
              to="/privacy?lang=pt"
              className={`privacy-lang-switch__link${i18n.language?.toLowerCase().startsWith('pt') ? ' is-active' : ''}`}
            >
              {safeT('legal.privacy.langPortuguese', 'Português')}
            </Link>
          </div>

          <h1 id="privacy-main-heading" className="about-marketing-title">
            {safeT('legal.privacy.pageHeading', 'Privacy Policy')}
          </h1>

          {SECTION_IDS.map((n) => {
            const titleKey = sectionKey(n, 'title');
            const bodyKey = sectionKey(n, 'body');
            const title = safeT(titleKey, '');
            const body = i18n.t(bodyKey, { ...interp, defaultValue: '' });
            if (!title && !body) return null;
            return (
              <section key={n} className="privacy-section" aria-labelledby={`privacy-h-${n}`}>
                {title ? (
                  <h2 id={`privacy-h-${n}`} className="about-marketing-subtitle">
                    {title}
                  </h2>
                ) : null}
                {body ? <BodyParagraphs text={body} /> : null}
              </section>
            );
          })}
        </article>
      </Container>
    </div>
  );
};

export default PrivacyPolicyPage;
